/**
 * ATL-1445 stage 3 — the backend write routes of domain W, split out of `wWriterRegistry.ts` (P8 review
 * 1, N-3) so the app bundle can ask "is this route W?" without carrying the registry's writer inventory.
 *
 * The bundle needs it at runtime: the v1 → v2 migration and the 409 conversion decide W membership of a
 * persisted request by its route (`v1ToV2.ts`). The registry re-exports everything here unchanged, and
 * `scripts/__tests__/wWriterRegistry.test.js` still holds these lists to the backend's fenced-table
 * evidence (a route that can write a fenced table and is in none of them fails CI there).
 *
 * Dependency-free, like the registry: no TypeScript compiler, no Firebase.
 */

/**
 * The backend write routes of domain W, by path prefix: each writes a fenced table. A function that
 * calls one itself (not only W writers write Firestore — `mirrorSaleFiscalInfo` writes W with NO
 * Firestore write) must be a W writer, a reviewed out-of-W writer (`wRouteException`), or a helper
 * called only by W writers (checked by the registry test). The set is not asserted by hand: the test
 * walks every backend controller route to the tables it can write (`backendFencedRouteEvidence`), and
 * a route that can write a fenced table must be here, in `W_ROUTES`, or in `W_ROUTE_EXCLUSIONS`.
 */
export const W_ROUTE_PREFIXES: readonly string[] = [
  '/sales', '/complex-sales', '/supplies', '/transfers', '/writeoffs', '/revisions', '/manufactures',
  '/products', '/product-categories', '/product-modifiers', '/clients', '/transactions', '/spendings',
  '/cashbox-transactions', '/working-hours', '/recurring-spendings', '/stores', '/suppliers', '/supply-orders',
  // reset_organization_data deletes books rows; marketplace-settings writes product_categories.
  '/organizations/reset-data', '/marketplace-settings',
  // The legacy bonus-history route inserts client_operations (fenced) — review 8.
  '/client-operations',
]

/**
 * W routes named exactly (`METHOD /path`), where the prefix would sweep in routes that write no books.
 * `POST /organizations` is signup: `create_organization_atomic` inserts the first store, and the
 * service seeds products, product_categories, suppliers, product_modifiers, recurring_spendings,
 * product_store_prices and product_store_stock — review 8.
 */
export const W_ROUTES: readonly string[] = ['POST /organizations']

/**
 * Backend routes that CAN write a fenced table but are not domain W, each with its reason. The test
 * refuses an exclusion that no evidence needs (stale), one that is also a W route, and any app call
 * to an excluded route (an exclusion is for routes the app never sends).
 */
export const W_ROUTE_EXCLUSIONS: ReadonlyArray<{ prefix?: string; route?: string; reason: string }> = [
  {
    prefix: '/migration',
    reason:
      'Operator-only Firestore-as-truth migration tooling behind MigrationTokenGuard (`x-migration-token`); ' +
      'refused for a Supabase-master org by WritesMasterService (409/503) and by the database fence ' +
      '(ATL-1488, write-cutover-fences.md). The app never calls it.',
  },
  {
    // Inside the `/products` prefix, so it is excluded by route (and `isWRoute` honours exclusions).
    route: 'POST /products/heal-missing',
    reason:
      'Operator-only bulk heal of products from FIRESTORE (ATL-794) behind MigrationTokenGuard ' +
      '(`x-migration-token`, @Public): a Firestore-as-truth tool, refused for a Supabase-master org by ' +
      'WritesMasterService (409/503) in every per-item heal. The app never calls it (#958 review 1, Q-B1-5).',
  },
  {
    route: 'POST /reconciliation/sweep',
    reason:
      'Operator-only reconciliation sweep behind MigrationTokenGuard; it skips a Supabase-master org ' +
      '(`write-master-supabase`) and is fenced by the database trigger. The app never calls it.',
  },
  {
    route: 'POST /sales-operator/lost-delete',
    reason:
      'Operator-only deletion of a lost-delete sale without its stock reversal (ATL-2000) behind ' +
      'MigrationTokenGuard (`x-migration-token`, @Public): a Firestore-as-truth tool (it admits a sale only ' +
      "on Firestore's sale+deleteSale ledger), refused for a Supabase-master org by WritesMasterService " +
      '(409/503) before any read, gated by assertBooksWriteMayExecute, and fenced by the database trigger. ' +
      'The app never calls it.',
  },
]

const routeMatches = (route: string, entry: { prefix?: string; route?: string }): boolean => {
  const path = route.split(' ')[1] ?? ''
  if (entry.route !== undefined) return route === entry.route
  const prefix = entry.prefix ?? ''
  return path === prefix || path.startsWith(`${prefix}/`)
}

/** The `W_ROUTE_EXCLUSIONS` entry a route falls under, if any. */
export const wRouteExclusion = (route: string): { prefix?: string; route?: string; reason: string } | undefined =>
  W_ROUTE_EXCLUSIONS.find((e) => routeMatches(route, e))

/** Does a `METHOD /path` route write domain W? An excluded route never does, even inside a W prefix. */
export const isWRoute = (route: string): boolean =>
  wRouteExclusion(route) === undefined &&
  (W_ROUTES.includes(route) || W_ROUTE_PREFIXES.some((prefix) => routeMatches(route, { prefix })))
